import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ClipboardList, Store, Coins, Target, BarChart3, Clock } from 'lucide-react';
import { DashboardSkeleton, ErrorState } from '../../components/skeletons';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revenueSort, setRevenueSort] = useState('newest'); // 'newest' or 'oldest'

  useEffect(() => {
    adminAPI.dashboard()
      .then(r => setData(r.data.data || r.data))
      .catch(e => setError(e.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardSkeleton />;

  if (error) return (
    <div style={{ padding: 24, maxWidth: 600, margin: '40px auto' }}>
      <ErrorState
        title="Failed to load dashboard"
        message={error}
        onRetry={() => {
          setLoading(true);
          setError('');
          adminAPI.dashboard()
            .then(r => setData(r.data.data || r.data))
            .catch(e => setError(e.response?.data?.message || 'Failed to load dashboard'))
            .finally(() => setLoading(false));
        }}
      />
    </div>
  );

  const stats = [
    { label: 'Active Demands', value: data?.activeDemands ?? 0, icon: <ClipboardList size={24} color={GREEN} />, color: GREEN },
    { label: 'Active Vendors', value: data?.activeVendors ?? 0, icon: <Store size={24} color={PURPLE} />, color: PURPLE },
    { label: 'Platform Revenue (MTD)', value: `₹${(data?.mtdRevenue ?? 0).toLocaleString('en-IN')}`, icon: <Coins size={24} color={GOLD} />, color: GOLD },
    { label: 'Leads Delivered', value: data?.mtdLeads ?? 0, icon: <Target size={24} color={GREEN} />, color: GREEN },
  ];

  const totalWallets = data?.totalVendors || 0;
  const paused = data?.pausedVendors || 0;
  const low = data?.lowBalance || 0;
  const healthy = Math.max(0, totalWallets - paused - low);

  const walletHealth = { healthy, low, paused };

  let revenueSources = data?.revenueBySrc || data?.revenuBySource || [];
  if (revenueSort === 'newest') {
    revenueSources = [...revenueSources].sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate));
  } else if (revenueSort === 'oldest') {
    revenueSources = [...revenueSources].sort((a, b) => new Date(a.lastDate) - new Date(b.lastDate));
  }

  return (
    <div style={{ padding: 24, background: CREAM, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR, margin: 0 }}>
            Admin Dashboard
          </h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>
            Welcome back, {user?.fullName?.split(' ')[0] || 'Admin'}
          </p>
        </div>
        <div style={{ fontSize: 12, color: MUTED, background: '#fff', padding: '8px 16px', borderRadius: 8, border: `1px solid ${BORDER}` }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {stats.map((s, i) => (
          <div key={i} className="card" style={{ padding: '20px 22px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
              <div style={{ fontSize: 28, opacity: 0.5 }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Wallet Health */}
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 700, color: CHAR, marginBottom: 20 }}>
            Wallet Health Breakdown
          </h3>

          {[
            { label: 'Healthy', count: walletHealth.healthy, color: GREEN, bg: '#dcfce7' },
            { label: 'Low Balance', count: walletHealth.low, color: GOLD, bg: '#fef3c7' },
            { label: 'Paused', count: walletHealth.paused, color: '#dc2626', bg: '#fee2e2' },
          ].map((w, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: CHAR }}>{w.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: w.color }}>{w.count}</span>
              </div>
              <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${totalWallets ? (w.count / totalWallets) * 100 : 0}%`,
                  height: '100%',
                  background: w.color,
                  borderRadius: 4,
                  transition: 'width 0.4s ease'
                }} />
              </div>
            </div>
          ))}

          <div style={{ marginTop: 20, padding: '12px 16px', background: '#f9fafb', borderRadius: 8, fontSize: 12, color: MUTED }}>
            Total wallets: <strong style={{ color: CHAR }}>{totalWallets}</strong>
          </div>
        </div>

        {/* Revenue by Source */}
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 700, color: CHAR, margin: 0 }}>
              Revenue by Source
            </h3>
            <button
              onClick={() => setRevenueSort(prev => prev === 'newest' ? 'oldest' : 'newest')}
              style={{
                background: 'none', border: `1px solid ${BORDER}`, borderRadius: 4,
                padding: '4px 8px', fontSize: 11, fontWeight: 600, color: MUTED,
                cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.target.style.background = '#f9fafb'; e.target.style.color = CHAR; }}
              onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = MUTED; }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Clock size={11} />
                {revenueSort === 'newest' ? 'Newest First' : 'Oldest First'}
              </span>
            </button>
          </div>

          {revenueSources.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: MUTED }}>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
                <BarChart3 size={36} color={MUTED} style={{ opacity: 0.6 }} />
              </div>
              <p style={{ fontSize: 13 }}>No revenue data available yet</p>
            </div>
          ) : (
            revenueSources.map((src, i) => {
              const maxVal = Math.max(...revenueSources.map(s => s.amount || s.value || 0));
              const val = src.amount || src.value || 0;
              const colors = [GREEN, PURPLE, GOLD, '#2563eb', '#dc2626'];
              return (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: CHAR }}>{src.source || src.label || src._id}</div>
                      <div style={{ fontSize: 10, color: MUTED }}>Last active: {new Date(src.lastDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: colors[i % colors.length] }}>
                      ₹{val.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: maxVal ? `${(val / maxVal) * 100}%` : '0%',
                      height: '100%',
                      background: colors[i % colors.length],
                      borderRadius: 4,
                    }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
